import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Spinner } from '@/components/spinner'
import { Button } from '@/components/ui/button'
import useTranslator from '@/hooks/translation'

import { useProjectsCreate } from './hooks'
import { ProjectsCreateStepOne } from './step-one'
import { ProjectsCreateStepTwo } from './step-two'
import { projectsCreateSchema, ProjectsCreateSchemaType } from './types'

export function ProjectsCreateForm({
  isModalOpen,
  setIsModalOpen,
}: {
  isModalOpen?: boolean
  setIsModalOpen?: (isModalOpen: boolean) => void
}) {
  const { handleChangeFormStep, getStepTitle } = useProjectsCreate(
    isModalOpen,
    setIsModalOpen,
  )

  const { getTranslation } = useTranslator()

  const [isLoadingStep, setIsLoadingStep] = useState<boolean>(false)

  const [imgPreview, setImgPreview] = useState<string>()

  const form = useForm<ProjectsCreateSchemaType>({
    mode: 'onChange',
    resolver: zodResolver(projectsCreateSchema),
    defaultValues: {
      currentStep: 1,
      name: '',
      picture: null,
      botIds: [],
    },
  })

  const {
    handleSubmit,
    getValues,
    formState: { isSubmitting, isDirty, isValid },
  } = form

  const { currentStep, name } = getValues()

  async function onSubmitCreateProject() {
    await handleChangeFormStep(1, setIsLoadingStep, form)
  }

  async function onChangeFormStep(changeFormStep: 1 | -1) {
    await handleChangeFormStep(changeFormStep, setIsLoadingStep, form)
  }

  const hasInsertedName = useMemo(() => {
    return name.trim() !== ''
  }, [name])

  const isDisabledButton = useMemo(() => {
    const loadingConditions = isSubmitting || isLoadingStep

    return loadingConditions || !isValid || !isDirty
  }, [isSubmitting, isLoadingStep, isValid, isDirty])

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSubmit(onSubmitCreateProject)}
        className="mx-auto flex w-full min-w-[200px] flex-col justify-between"
      >
        <div>
          {currentStep === 1 && (
            <ProjectsCreateStepOne
              imgPreview={imgPreview}
              setImgPreview={setImgPreview}
            />
          )}

          {currentStep === 2 && <ProjectsCreateStepTwo />}
        </div>

        <div className="flex w-full items-center justify-between bg-dialog-actions-background p-6 px-8 md:rounded-b-3xl">
          {currentStep === 1 && (
            <Button
              type="button"
              variant="fintalk"
              className="ml-auto"
              disabled={isDisabledButton || !hasInsertedName}
              onClick={() => onChangeFormStep(1)}
            >
              {getStepTitle(currentStep)}

              {isSubmitting || isLoadingStep ? (
                <Spinner className="text-fintalk-foreground" />
              ) : (
                <ChevronRight />
              )}
            </Button>
          )}

          {currentStep >= 2 && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onChangeFormStep(-1)}
              disabled={isSubmitting || isLoadingStep}
            >
              <ChevronLeft />
              {getTranslation('common.back')}
            </Button>
          )}

          {currentStep === 2 && (
            <Button
              type="submit"
              variant="fintalk"
              className="ml-auto"
              disabled={isDisabledButton || !hasInsertedName}
            >
              {(isSubmitting || isLoadingStep) && (
                <Spinner className="text-fintalk-foreground" />
              )}
              {getStepTitle(currentStep)}
            </Button>
          )}
        </div>
      </form>
    </FormProvider>
  )
}
